import { z } from "zod";
import { memberDueStatusSchema } from "./collections.js";

/* ===========================================================================
 * US-E6.F7-0045 — My Payment Status (@UC0058)
 * GET /v1/funds/:id/dues/me — dues của CHÍNH member (IDOR: userId từ token).
 * Read-only, KHÔNG post ledger. Tiền STRING minor unit (B1, no float).
 * ========================================================================= */

const MONEY_INTEGER_STRING_PATTERN = /^\d+$/;

/** 1 đợt thu của tôi + trạng thái đóng (status passthrough từ member_dues, KHÔNG recompute). */
export const myDueItemSchema = z
  .object({
    memberDueId: z.string().uuid(),
    collectionId: z.string().uuid(),
    collectionName: z.string().nullable(),
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /**
     * US-E24.F4-0353-S01 (@UC9569) — số ĐÃ đóng tích luỹ cho due này (STRING minor).
     *
     * Dữ liệu đã có từ US-E16.F4-0185 (`member_dues.paidAmountMinor` + CHECK `0 ≤ paid ≤ amount`) và
     * `record-paid` ghi nó thật; contract cũ không phơi ⇒ màn "Khoản phải đóng của tôi" không có cách
     * nào vẽ *"đã đóng 150k/500k"*. Due chưa đóng ⇒ `"0"` (SỰ THẬT đo được), KHÔNG `null`/vắng field.
     *
     * 🔴 Client KHÔNG tự tính "còn nợ = amount − paid": JS không có int64 an toàn ⇒ phép trừ trên
     * client là phép trừ trên float. Cần "còn nợ" thì server trả — và chỉ khi có màn thật dùng tới.
     */
    paidAmountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    status: memberDueStatusSchema,
    dueDate: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();

export type MyDueItem = z.infer<typeof myDueItemSchema>;

export const myPaymentStatusResponseSchema = z
  .object({
    /**
     * Tổng đã đóng — STRING minor unit.
     * = Σ amountMinor của (paid + paid_on_behalf) **+** Σ paidAmountMinor của (partially_paid).
     */
    totalPaidMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /**
     * Tổng còn nợ — STRING minor unit.
     * = Σ amountMinor của (pending + overdue + awaiting_confirmation) **+**
     *   Σ (amountMinor − paidAmountMinor) của (partially_paid).
     * 🔴 `awaiting_confirmation` nằm ở ĐÂY, không ở `totalPaid` (§3.7 — "đã chuyển" ≠ "đã nhận").
     */
    totalOwedMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Tổng được miễn (waived) — STRING minor unit. Tách riêng để client reconcile:
     *  paid + owed + waived = Σ(mọi due). KHÔNG gộp vào paid/owed (miễn ≠ đã đóng ≠ còn nợ). */
    totalWaivedMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    dues: z.array(myDueItemSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();

export type MyPaymentStatusResponse = z.infer<
  typeof myPaymentStatusResponseSchema
>;
